import React, { useState, useEffect } from 'react';
import { BankAccount, CreateBankAccountRequest, UpdateBankAccountRequest } from '../types';

interface BankAccountFormProps {
    bankAccount?: BankAccount | null;
    onSubmit: (data: CreateBankAccountRequest | UpdateBankAccountRequest) => void;
    onCancel: () => void;
}

const BankAccountForm: React.FC<BankAccountFormProps> = ({ bankAccount, onSubmit, onCancel }) => {
    const [formData, setFormData] = useState({
        name: '',
        initialBalance: 0,
        currentBalance: 0,
        isVault: false
    });
    
    const [initialBalanceInput, setInitialBalanceInput] = useState<string>('');
    const [currentBalanceInput, setCurrentBalanceInput] = useState<string>('');

    const formatBRL = (value: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    
    const parseToNumber = (raw: string) => {
        const onlyDigits = raw.replace(/\D/g, '');
        if (!onlyDigits) return 0;
        // interpret as cents of BRL mask input (e.g., 10000 => 100,00)
        const cents = parseInt(onlyDigits, 10);
        return cents / 100;
    };

    useEffect(() => {
        if (bankAccount) {
            // Converter centavos para reais para exibição
            const initialReais = (bankAccount.initialBalance || 0) / 100;
            const currentReais = (bankAccount.currentBalance || 0) / 100;
            
            setFormData({
                name: bankAccount.name,
                initialBalance: initialReais,
                currentBalance: currentReais,
                isVault: bankAccount.isVault || false
            });
            
            setInitialBalanceInput(initialReais ? formatBRL(initialReais) : '');
            setCurrentBalanceInput(currentReais ? formatBRL(currentReais) : '');
        } else {
            // Inicializar com valores vazios para modo de criação
            setInitialBalanceInput('');
            setCurrentBalanceInput('');
        }
    }, [bankAccount]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        // Converter reais para centavos antes de enviar
        const initialCents = Math.round(parseToNumber(initialBalanceInput) * 100);
        const currentCents = Math.round(parseToNumber(currentBalanceInput) * 100);
        
        const payload: CreateBankAccountRequest | UpdateBankAccountRequest = {
            name: formData.name,
            initialBalance: initialCents,
            ...(bankAccount ? { currentBalance: currentCents } : { isVault: formData.isVault })
        };
        onSubmit(payload);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    const handleInitialBalanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const numberValue = parseToNumber(raw);
        setInitialBalanceInput(formatBRL(numberValue));
        setFormData(prev => ({ ...prev, initialBalance: numberValue }));
    };
    
    const handleCurrentBalanceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value;
        const numberValue = parseToNumber(raw);
        setCurrentBalanceInput(formatBRL(numberValue));
        setFormData(prev => ({ ...prev, currentBalance: numberValue }));
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label htmlFor="name" className="block text-sm font-medium text-secondary-700 mb-1">
                    Nome *
                </label>
                <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="input-field"
                    placeholder="Digite o nome da conta"
                />
            </div>

            <div>
                <label htmlFor="initialBalance" className="block text-sm font-medium text-secondary-700 mb-1">
                    Saldo Inicial *
                </label>
                <input
                    type="text"
                    inputMode="numeric"
                    id="initialBalance"
                    name="initialBalanceMasked"
                    value={initialBalanceInput}
                    onChange={handleInitialBalanceChange}
                    required
                    className="input-field"
                    placeholder="R$ 0,00"
                />
            </div>

            {!bankAccount && (
                <div>
                    <label className="flex items-center space-x-2">
                        <input
                            type="checkbox"
                            checked={formData.isVault}
                            onChange={(e) => setFormData(prev => ({ ...prev, isVault: e.target.checked }))}
                            className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm font-medium text-secondary-700">Caixinha</span>
                    </label>
                    <p className="mt-1 text-xs text-secondary-500">Esta opção só pode ser definida na criação da conta</p>
                </div>
            )}

            {bankAccount && (
                <div>
                    <label htmlFor="currentBalance" className="block text-sm font-medium text-secondary-700 mb-1">
                        Saldo Atual *
                    </label>
                    <input
                        type="text"
                        inputMode="numeric"
                        id="currentBalance"
                        name="currentBalanceMasked"
                        value={currentBalanceInput}
                        onChange={handleCurrentBalanceChange}
                        required
                        className="input-field"
                        placeholder="R$ 0,00"
                    />
                </div>
            )}

            <div className="flex justify-end space-x-3 pt-4">
                <button
                    type="button"
                    onClick={onCancel}
                    className="btn-secondary"
                >
                    Cancelar
                </button>
                <button
                    type="submit"
                    className="btn-primary"
                >
                    {bankAccount ? 'Atualizar' : 'Criar'}
                </button>
            </div>
        </form>
    );
};

export default BankAccountForm;




